import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AppDateInput from '../../../components/AppDateInput'
import AppTimeInput from '../../../components/AppTimeInput'
import AppDropdown from '../../../components/AppDropdown'
import AppCombobox from '../../../components/AppCombobox'
import AppScrollArea from '../../../components/AppScrollArea'
import AppToggle from '../../../components/AppToggle'
import { AppTooltip } from '../../../components/AppTooltip'
import AppDocumentPreviewModal from '../../../components/AppDocumentPreviewModal'
import { useInfiniteOptions } from '../../../hooks/useInfiniteOptions'
import { useSearchParams } from 'react-router-dom'
import { fetchJobOptionsPage, fetchJobUserOptionsPage, fetchTaskOptionsPage } from './api/jobApi'
import { useJobEntryScreen } from './hooks/useJobEntryScreen'
import { appToast } from '../../../components/AppToast'
import { useRefreshEmployeeAvailabilityMutation } from '../../../api/serverStateHooks'
import AllocationAssignmentModal from '../../../features/allocation/components/AllocationAssignmentModal'

function statusChipClass(status) {
  if (status === 'IN_PROGRESS') return 'text-primary'
  if (status === 'PENDING_REVIEW') return 'text-amber-600'
  if (status === 'COMPLETED') return 'text-emerald-600'
  if (status === 'CLOSED') return 'text-error'
  return 'text-on-surface-variant'
}

function initials(name) {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function toPhotoUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`
  return `${API_BASE_URL}/${path}`
}

function attachmentDisplayName(item) {
  if (!item) return ''
  const fromMeta = String(item.originalName || item.name || '').trim()
  if (fromMeta) return fromMeta
  const path = String(item.path || '').trim()
  return path.split('/').pop()?.split('\\').pop() || path
}

function clientRpRoleLabel(row) {
  const role = String(row?.role || '').trim()
  if (!role) return 'CONTACT'
  return role.replaceAll('_', ' ')
}

function clientRpNameLabel(row) {
  const name = String(row?.name || '').trim()
  if (name) return name
  const email = String(row?.email || '').trim()
  if (email) return email
  const phone = String(row?.phone || '').trim()
  if (phone) return phone
  return 'Unknown contact'
}

function normalizeResponsibleDraft(rows, userNameById = new Map()) {
  if (!Array.isArray(rows)) return []
  const seen = new Set()
  const normalized = []
  rows.forEach((row, index) => {
    const userId = Number(row?.user_id)
    if (!Number.isFinite(userId) || userId <= 0 || seen.has(userId)) return
    seen.add(userId)
    normalized.push({
      user_id: userId,
      user_name: String(userNameById.get(String(userId)) || row?.user_name || 'User').trim() || 'User',
      priority_order: Number(row?.priority_order || index + 1),
      assignment_note: String(row?.assignment_note || ''),
      is_completed: Number(row?.is_completed) ? 1 : 0,
    })
  })
  normalized.sort((a, b) => Number(a.priority_order || 0) - Number(b.priority_order || 0))
  return normalized.map((row, index) => ({ ...row, priority_order: index + 1 }))
}

function toDecimalHours(hoursValue, minutesValue) {
  const hours = Math.max(0, Math.floor(Number(hoursValue || 0) || 0))
  const minutesRaw = Math.floor(Number(minutesValue || 0) || 0)
  const minutes = Math.min(59, Math.max(0, minutesRaw))
  const total = hours + minutes / 60
  return Number(total.toFixed(2))
}

export default function JobConfigurationScreen() {
  const { state, actions } = useJobEntryScreen()
  const [searchParams] = useSearchParams()
  const markCompleteMode = searchParams.get('mode') === 'markComplete'
  const sourceTimesheetId = searchParams.get('timesheetId')

  const markCompleteToastFired = useRef(false)

  useEffect(() => {
    if (markCompleteMode && sourceTimesheetId && !markCompleteToastFired.current) {
      markCompleteToastFired.current = true
      appToast.info(
        'Timesheet submitted! You can now mark this job as completed. Set the completion note and click "Mark Completed".'
      )
    }
  }, [markCompleteMode, sourceTimesheetId])

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  useEffect(() => {
    if (state.optionError) {
      appToast.warning(state.optionError)
    }
  }, [state.optionError])

  const selectedProjectId = Number(state.resolvedProjectId || 0)
  const fetchProjectScopedUsers = useCallback(
    (search, page, pageSize) => {
      return fetchJobUserOptionsPage(search, page, pageSize, selectedProjectId || null)
    },
    [selectedProjectId],
  )
  const [isDependencyModalOpen, setIsDependencyModalOpen] = useState(false)
  const [isResponsiblePickerOpen, setIsResponsiblePickerOpen] = useState(false)
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false)
  const [refreshEmployee, { isLoading: isRefreshingEmployee }] = useRefreshEmployeeAvailabilityMutation()
  const [draftResponsibleDependencies, setDraftResponsibleDependencies] = useState([])
  const [responsibleModalError, setResponsibleModalError] = useState('')
  const [commentDraft, setCommentDraft] = useState('')
  const [uploadInputKey, setUploadInputKey] = useState(0)
  const [previewingFile, setPreviewingFile] = useState(null) // { url, name }


  // Infinite scroll options for Job Dependencies modal
  const {
    items: rawDepJobs,
    search: depSearch,
    setSearch: setDepSearch,
    isLoading: depIsLoading,
    hasMore: depHasMore,
    sentinelRef: depSentinelRef,
  } = useInfiniteOptions({
    fetchOptions: fetchJobOptionsPage,
    enabled: isDependencyModalOpen,
  })

  const {
    items: rawResponsibleOptions,
    search: responsibleSearch,
    setSearch: setResponsibleSearch,
    isLoading: responsibleIsLoading,
    hasMore: responsibleHasMore,
    sentinelRef: responsibleSentinelRef,
  } = useInfiniteOptions({
    fetchOptions: fetchProjectScopedUsers,
    enabled: isResponsiblePickerOpen,
  })

  // Filter out the currently editing job from candidates
  const depJobs = useMemo(
    () => rawDepJobs.filter((job) => Number(job.value) !== Number(state.editingJobId || 0)),
    [rawDepJobs, state.editingJobId]
  )

  const userNameById = useMemo(() => {
    const map = new Map()
      ; (Array.isArray(state.userOptions) ? state.userOptions : []).forEach((user) => {
        map.set(String(user.id), String(user.name || '').trim())
      })
      ; (Array.isArray(rawResponsibleOptions) ? rawResponsibleOptions : []).forEach((user) => {
        map.set(String(user.value), String(user.label || '').trim())
      })
    return map
  }, [rawResponsibleOptions, state.userOptions])

  const responsibleUsers = useMemo(() => {
    const rows = Array.isArray(state.form.responsible_dependencies) ? state.form.responsible_dependencies : []
    return rows.map((row, index) => {
      const id = Number(row?.user_id)
      const resolvedName = userNameById.get(String(id)) || row?.user_name || 'User'
      return {
        user_id: id,
        user_name: resolvedName,
        priority_order: Number(row?.priority_order || index + 1),
        assignment_note: String(row?.assignment_note || ''),
        is_completed: Number(row?.is_completed) ? 1 : 0,
      }
    })
  }, [state.form.responsible_dependencies, userNameById])

  const draftResponsibleUsers = useMemo(
    () => normalizeResponsibleDraft(draftResponsibleDependencies, userNameById),
    [draftResponsibleDependencies, userNameById],
  )

  function openResponsiblePicker() {
    setDraftResponsibleDependencies(normalizeResponsibleDraft(state.form.responsible_dependencies, userNameById))
    setResponsibleModalError('')
    setIsResponsiblePickerOpen(true)
  }

  function cancelResponsiblePicker() {
    setDraftResponsibleDependencies([])
    setResponsibleModalError('')
    setIsResponsiblePickerOpen(false)
  }

  function toggleDraftResponsibleUser(user) {
    const userId = Number(user?.value)
    if (!Number.isFinite(userId) || userId <= 0) return

    setResponsibleModalError('')
    setDraftResponsibleDependencies((prev) => {
      const existing = normalizeResponsibleDraft(prev, userNameById)
      const selected = existing.some((row) => Number(row.user_id) === userId)
      if (selected) {
        return normalizeResponsibleDraft(existing.filter((row) => Number(row.user_id) !== userId), userNameById)
      }
      return normalizeResponsibleDraft(
        [
          ...existing,
          {
            user_id: userId,
            user_name: String(user?.label || userNameById.get(String(userId)) || 'User').trim() || 'User',
            assignment_note: '',
            is_completed: 0,
            priority_order: existing.length + 1,
          },
        ],
        userNameById,
      )
    })
  }

  function setDraftResponsibleNote(userId, note) {
    const normalizedId = Number(userId)
    setResponsibleModalError('')
    setDraftResponsibleDependencies((prev) =>
      normalizeResponsibleDraft(
        prev.map((row) =>
          Number(row.user_id) === normalizedId
            ? {
              ...row,
              assignment_note: String(note || ''),
            }
            : row,
        ),
        userNameById,
      ),
    )
  }

  function saveResponsiblePicker() {
    const normalized = normalizeResponsibleDraft(draftResponsibleDependencies, userNameById)
    const missingNote = normalized.find((row) => !String(row.assignment_note || '').trim())
    if (missingNote) {
      setResponsibleModalError('Responsible note required.')
      return
    }

    actions.patchForm('responsible_dependencies', normalized)
    setResponsibleModalError('')
    setIsResponsiblePickerOpen(false)
    setDraftResponsibleDependencies([])
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            {state.jobCode ? (
              <span className="rounded-full bg-primary-fixed px-3 py-1 text-sm font-bold uppercase tracking-widest text-primary">
                {state.jobCode}
              </span>
            ) : null}
            {state.overrunFlag === 'RED' && (
              <AppTooltip content="Overrun: actual hours exceeded average by more than 20%">
                <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700 cursor-help">
                  <span className="material-symbols-outlined text-[10px]">warning</span>
                  Overrun
                </span>
              </AppTooltip>
            )}
            {state.overrunFlag === 'GREEN' && (
              <AppTooltip content="Efficient: actual hours were below the benchmark average">
                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 cursor-help">
                  <span className="material-symbols-outlined text-[10px]">verified</span>
                  Efficient
                </span>
              </AppTooltip>
            )}
            <div className="flex items-center gap-1 text-on-surface-variant">
              <span className="material-symbols-outlined text-sm">schedule</span>
              <span className="text-sm font-medium">Last updated 2 hours ago</span>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-on-surface md:text-6xl">
            {state.form.job_name || (state.isEditMode ? 'Job Configuration' : 'New Job Entry')}
          </h1>
        </div>

        {/* <div className="flex items-center gap-3">
          {state.isEditMode ? (
            <button
              type="button"
              onClick={actions.handleArchive}
              disabled={state.isSaving}
              className="rounded-md px-6 py-2.5 text-lg font-semibold text-secondary transition-colors hover:bg-surface-container-high disabled:opacity-50"
            >
              <span className="mr-2 material-symbols-outlined align-middle text-lg">archive</span>
              Archive
            </button>
          ) : null}
          <button
            type="button"
            onClick={actions.handleDiscard}
            className="rounded-md bg-secondary-container px-6 py-2.5 text-lg font-bold text-on-secondary-container hover:bg-surface-container-highest"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading}
            className="rounded-md bg-primary px-8 py-2.5 text-lg font-bold text-on-primary shadow-lg shadow-primary/20 disabled:opacity-60"
          >
            <span className="mr-2 material-symbols-outlined align-middle text-lg">save</span>
            {state.isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div> */}
      </header>



      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading job configuration...
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <section className="rounded-lg bg-surface-container-low p-8">
              <div className="mb-8 flex items-center justify-between">
                <h2 className="text-3xl font-bold tracking-tight text-on-surface">Core Information</h2>
                <div className="flex items-center gap-2 text-on-surface-variant">
                  <span className="material-symbols-outlined">info</span>
                  <span className="text-xs font-bold uppercase tracking-wider">Required Fields</span>
                </div>
              </div>

              <div className="space-y-8">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                      Job Code <span className="material-symbols-outlined text-[14px]">lock</span>
                    </span>
                    <input
                      type="text"
                      value={state.jobCode || 'AUTO-GENERATED'}
                      readOnly
                      className="w-full cursor-not-allowed rounded-md border-none bg-surface-variant/30 px-4 py-3 font-medium text-on-surface-variant"
                    />
                  </label>

                  <label className="space-y-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                      Job Name <span className="text-error">*</span>
                    </span>
                    <input
                      type="text"
                      value={state.form.job_name}
                      onChange={(event) => actions.patchForm('job_name', event.target.value)}
                      placeholder="Enter job title"
                      className="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-on-surface shadow-sm ring-1 ring-outline-variant/20 outline-none transition-all focus:ring-2 focus:ring-primary/40"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                  <label className="space-y-2 md:col-span-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                      Parent Task <span className="text-error">*</span>
                    </span>
                    <AppCombobox
                      value={state.form.task_id}
                      onChange={(value) => actions.patchForm('task_id', value)}
                      placeholder="Select Task"
                      searchPlaceholder="Search tasks..."
                      fetchOptions={fetchTaskOptionsPage}
                      selectedLabelOverride={state.timelineMeta.taskName}
                      minSearchLength={0}
                      triggerClassName="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-on-surface shadow-sm ring-1 ring-outline-variant/20"
                    />
                  </label>

                  <label className="space-y-2 md:col-span-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Description</span>
                    <textarea
                      rows={4}
                      value={state.form.notes}
                      onChange={(event) => actions.patchForm('notes', event.target.value)}
                      className="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-on-surface shadow-sm ring-1 ring-outline-variant/20 outline-none transition-all focus:ring-2 focus:ring-primary/40"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-8 pt-4 md:grid-cols-2">
                  <div className="space-y-4">
                    <h3 className="border-l-4 border-primary pl-3 text-sm font-extrabold uppercase tracking-widest text-primary">
                      Internal RP <span className="text-error">*</span>
                    </h3>
                    <div className="rounded-lg bg-surface-container-lowest p-4 shadow-sm ring-1 ring-outline-variant/20">
                      <label className="mb-3 block space-y-2">
                        {/* <span className="text-xs font-semibold text-on-surface-variant">Infrastructure Lead</span> */}
                        <AppDropdown
                          value={state.form.assigned_to}
                          onChange={(value) => actions.patchForm('assigned_to', value)}
                          placeholder="Select internal RP"
                          triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface"
                          options={[
                            { value: '', label: 'Unassigned (Send to Job Pool)' },
                            ...state.userOptions.map((user) => ({
                              value: String(user.id),
                              label: user.name,
                            })),
                          ]}
                        />
                      </label>

                      {state.selectedAssignee ? (
                        <div className="flex items-center gap-3 rounded-md bg-surface-container-low p-3">
                          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-fixed text-base font-bold text-primary">
                            {initials(state.selectedAssignee.name)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[1.15rem] font-bold text-on-surface">{state.selectedAssignee.name}</p>
                            <p className="truncate text-sm text-on-surface-variant">{state.selectedAssignee.email}</p>
                            <p className="truncate text-xs text-on-surface-variant">
                              User ID: {state.selectedAssignee.employee_id || state.selectedAssignee.id}
                            </p>
                          </div>
                          <AppTooltip content="View resource availability and assignment">
                            <button
                              type="button"
                              onClick={() => setIsAvailabilityModalOpen(true)}
                              className="material-symbols-outlined text-primary transition-colors hover:text-primary-dark cursor-pointer flex items-center justify-center p-1 rounded-full hover:bg-surface-container-high"
                              style={{ fontVariationSettings: "'FILL' 1" }}
                            >
                              schedule
                            </button>
                          </AppTooltip>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2.5 rounded-md bg-primary/5 border border-dashed border-primary/30 p-3 text-sm text-primary">
                          <span className="material-symbols-outlined text-base">inbox</span>
                          <div>
                            <p className="font-bold text-xs text-on-surface">Unassigned (Job Pool)</p>
                            <p className="text-[11px] text-on-surface-variant font-medium">Job will be placed into the Job Pool for allocation</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="border-l-4 border-primary pl-3 text-sm font-extrabold uppercase tracking-widest text-primary">Client RP</h3>
                    <div className="rounded-lg bg-surface-container-lowest p-4 shadow-sm ring-1 ring-outline-variant/20">
                      <label className="mb-3 block space-y-2">
                        {/* <span className="text-xs font-semibold text-on-surface-variant">Client Contact</span> */}
                        <AppDropdown
                          value={state.form.client_rp_key}
                          onChange={(value) => actions.patchForm('client_rp_key', value)}
                          placeholder="Select client RP"
                          triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface"
                          options={state.clientRpOptions.map((row) => ({
                            value: `${String(row.role || '').trim().toUpperCase()}|${String(row.email || '').trim().toLowerCase()}|${String(row.phone || '').trim()}`,
                            label: `${clientRpNameLabel(row)} (${clientRpRoleLabel(row)})`,
                          }))}
                        />
                      </label>

                      {state.selectedClientRp ? (
                        <div className="rounded-md bg-surface-container-low p-3">
                          <p className="truncate text-[1.15rem] font-bold text-on-surface">{clientRpNameLabel(state.selectedClientRp)}</p>
                          <p className="truncate text-xs text-on-surface-variant">{clientRpRoleLabel(state.selectedClientRp)}</p>
                          <div className="mt-2 grid gap-1 text-xs text-on-surface-variant">
                            <span className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-sm">mail</span>
                              {String(state.selectedClientRp.email || '').trim() || '-'}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-sm">call</span>
                              {String(state.selectedClientRp.phone || '').trim() || '-'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-md bg-surface-container-low p-3 text-sm text-on-surface-variant">
                          Choose from this project&apos;s contacts.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-lg bg-surface-container-low p-8">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-3xl font-bold tracking-tight">Technical Assets &amp; Attachments</h2>
                <button
                  type="button"
                  onClick={() => document.getElementById(`job-attachment-input-${uploadInputKey}`)?.click()}
                  disabled={state.isUploadingAttachment}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-bold text-primary hover:bg-primary/10"
                >
                  <span className="material-symbols-outlined text-base">upload_file</span>
                  {state.isUploadingAttachment ? 'Uploading...' : 'Upload New'}
                </button>
                <input
                  id={`job-attachment-input-${uploadInputKey}`}
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.pdf,.docx"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) actions.handleUploadAttachment(file)
                    setUploadInputKey((prev) => prev + 1)
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {state.attachmentItems.length ? (
                  state.attachmentItems.map((item) => {
                    const displayName = attachmentDisplayName(item)
                    const lower = displayName.toLowerCase()
                    const isPdf = lower.endsWith('.pdf')
                    return (
                      <div key={item.id || item.path} className="flex items-center justify-between gap-3 rounded-md bg-surface-container-lowest p-3 ring-1 ring-outline-variant/20">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className={`material-symbols-outlined rounded-full p-2 ${isPdf ? 'bg-error-container text-error' : 'bg-primary-fixed text-primary'}`}>
                            {isPdf ? 'picture_as_pdf' : 'description'}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-lg font-semibold text-on-surface">{displayName}</p>
                            <p className="text-xs uppercase tracking-wider text-on-surface-variant">Attachment</p>
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          {item.path ? (
                            <button
                              type="button"
                              onClick={() => setPreviewingFile({ url: toPhotoUrl(item.path), name: displayName })}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary"
                              title="View Document"
                              aria-label={`View ${displayName}`}
                            >
                              <span className="material-symbols-outlined text-base">visibility</span>
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => actions.removeAttachment(item.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-error"
                            aria-label={`Remove ${displayName}`}
                            title="Remove Attachment"
                          >
                            <span className="material-symbols-outlined text-base">close</span>
                          </button>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="rounded-md bg-surface-container-lowest p-4 text-sm text-on-surface-variant">
                    No assets added yet.
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-lg bg-surface-container-low p-8">
              <h2 className="mb-8 text-3xl font-bold tracking-tight">Remarks &amp; Collaboration</h2>
              <div className="space-y-6">
                {state.isRemarksLoading ? (
                  <div className="rounded-md bg-surface-container-lowest p-4 text-sm text-on-surface-variant">Loading remarks...</div>
                ) : state.remarks.length ? (
                  state.remarks.map((item) => (
                    <div key={item.remark_id} className="flex gap-4">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-tertiary-container text-[10px] font-bold text-white">
                        {item.author?.profile_photo_path ? (
                          <img src={toPhotoUrl(item.author.profile_photo_path)} alt={item.author?.name || 'User'} className="h-full w-full object-cover" />
                        ) : (
                          initials(item.author?.name || 'U')
                        )}
                      </div>
                      <div className="flex-1 rounded-lg bg-surface-container-lowest p-4 shadow-sm">
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-sm font-bold">{item.author?.name || 'Unknown User'}</span>
                          <span className="text-[10px] text-on-surface-variant">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</span>
                        </div>
                        <p className="text-sm text-on-surface-variant">{item.message}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-md bg-surface-container-lowest p-4 text-sm text-on-surface-variant">No remarks yet.</div>
                )}

                <div className="flex gap-4">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-fixed text-[10px] font-bold text-primary">
                    {initials(state.selectedAssignee?.name || 'U')}
                  </div>
                  <div className="flex-1">
                    <textarea
                      rows={3}
                      value={commentDraft}
                      onChange={(event) => setCommentDraft(event.target.value)}
                      placeholder="Add a remark..."
                      className="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-inner ring-1 ring-outline-variant/10 outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        disabled={!state.isEditMode || state.isPostingRemark || !commentDraft.trim()}
                        onClick={async () => {
                          const text = commentDraft.trim()
                          if (!text) return
                          await actions.postRemark(text)
                          setCommentDraft('')
                        }}
                        className="rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest text-primary hover:bg-primary-container/10"
                      >
                        {state.isPostingRemark ? 'Posting...' : 'Post Comment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="col-span-12 space-y-6 lg:col-span-4">
            <section className="rounded-lg bg-primary-container p-8 text-white shadow-xl shadow-primary/20">
              <div className="mb-8 flex items-center justify-between">
                <div className="rounded-lg bg-white/20 p-2">
                  <span className="material-symbols-outlined">trending_up</span>
                </div>
                <span className="text-xs font-black uppercase tracking-tighter text-on-primary-container">Active Job</span>
              </div>

              <div className="mb-8 space-y-1">
                <p className="text-sm font-medium opacity-80">Overall Progress</p>
                <h3 className="text-5xl font-black">{state.completionPercent}%</h3>
              </div>

              <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-white/20">
                <div className="h-full rounded-full bg-white" style={{ width: `${Math.max(6, state.completionPercent)}%` }} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Status</p>
                  <div className="flex items-center gap-1.5">
                    <p className={`font-bold ${statusChipClass(state.form.status)}`}>{state.form.status.replaceAll('_', ' ')}</p>
                    {state.overrunFlag === 'RED' && (
                      <AppTooltip content="Overrun: actual hours exceeded average by more than 20%">
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700 cursor-help shrink-0">
                          <span className="material-symbols-outlined text-[10px]">warning</span>
                          Overrun
                        </span>
                      </AppTooltip>
                    )}
                    {state.overrunFlag === 'GREEN' && (
                      <AppTooltip content="Efficient: actual hours were below the benchmark average">
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 cursor-help shrink-0">
                          <span className="material-symbols-outlined text-[10px]">verified</span>
                          Efficient
                        </span>
                      </AppTooltip>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Points</p>
                  <p className="font-bold">
                    {Number(state.isEditMode ? state.pointsPreview?.netPoints : state.pointsPreview?.basePoints || 0)} pts
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-lg bg-surface-container-low p-8">
              <h2 className="mb-6 text-sm font-extrabold uppercase tracking-widest text-on-surface-variant">Schedule &amp; Metrics</h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between rounded-md bg-surface-container-lowest p-4">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase text-on-surface-variant">
                        Job Execution Date <span className="text-error">*</span>
                      </p>
                      <AppDateInput
                        value={state.form.planned_start}
                        onChange={(value) => {
                          actions.patchForm('planned_start', value)
                        }}
                        inputClassName="border-none bg-transparent p-0 pr-8 text-sm font-bold shadow-none"
                        buttonClassName="hover:bg-surface-container-low"
                      />
                    </div>
                  </div>
                  {state.form.planned_end && state.form.planned_start && state.form.planned_end !== state.form.planned_start ? (
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-fixed/30 px-2.5 py-0.5 text-[10px] font-bold text-primary">
                        <span className="material-symbols-outlined text-[12px]">nights_stay</span>
                        Spans Overnight
                      </span>
                      <p className="mt-1 text-[10px] font-semibold text-on-surface-variant">
                        Ends: {state.form.planned_end}
                      </p>
                    </div>
                  ) : null}
                </div>

                {/* ── Time row ── */}
                <div className="flex items-center justify-between rounded-md bg-surface-container-lowest p-4">
                  <div className="flex items-center gap-3">
                    {/* <span className="material-symbols-outlined text-primary">schedule</span> */}
                    <div>
                      <p className="text-[10px] font-bold uppercase text-on-surface-variant mb-1">
                        Start Time
                      </p>
                      <AppTimeInput
                        value={state.form.planned_start_time || ''}
                        onChange={(val) => actions.patchForm('planned_start_time', val)}
                        inputClassName="border-none bg-transparent p-0 pr-8 text-sm font-bold shadow-none"
                        buttonClassName="hover:bg-surface-container-low"
                      />
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-outline-variant">arrow_forward</span>
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase text-on-surface-variant mb-1">
                      End Time
                    </p>
                    <AppTimeInput
                      value={state.form.planned_end_time || ''}
                      onChange={(val) => actions.patchForm('planned_end_time', val)}
                      inputClassName="border-none bg-transparent p-0 pr-8 text-right text-sm font-bold shadow-none"
                      buttonClassName="hover:bg-surface-container-low"
                    />
                  </div>
                </div>

                {state.isPostponedExecutionDate ? (
                  <label className="grid gap-2 rounded-md bg-surface-container-lowest p-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                      Rollover Reason <span className="text-error">*</span>
                    </span>
                    <input
                      type="text"
                      value={state.form.rollover_reason}
                      onChange={(event) => actions.patchForm('rollover_reason', event.target.value)}
                      placeholder="Required because job execution date was postponed"
                      className="rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                    />
                  </label>
                ) : null}

                <label className="space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                    Estimated Time <span className="text-error">*</span>
                  </span>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Hours</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={state.form.planned_hours_value}
                        onChange={(event) => actions.setPlannedHours(event.target.value)}
                        className="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none"
                        placeholder="Hours"
                      />
                    </div>
                    <div className="grid gap-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Minutes</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        value={state.form.planned_minutes_value}
                        onChange={(event) => actions.setPlannedMinutes(event.target.value)}
                        className="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none"
                        placeholder="Minutes"
                      />
                    </div>
                  </div>
                </label>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Logged Hours</span>
                    <span className="font-bold text-on-surface">
                      {state.loggedHours.toFixed(2)} / {toDecimalHours(state.form.planned_hours_value, state.form.planned_minutes_value).toFixed(2)} est. hrs
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${state.loggedHoursProgress}%` }} />
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-lg bg-surface-container-low p-8">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-sm font-extrabold uppercase tracking-widest text-on-surface-variant">Dependencies</h2>
                <span className="rounded bg-error-container px-2 py-0.5 text-[10px] font-black text-on-error-container">
                  {state.selectedDependencyJobs.length} BLOCKERS
                </span>
              </div>
              <div className="space-y-3">
                {state.selectedDependencyJobs.length ? (
                  state.selectedDependencyJobs.map((job) => {
                    const isDone = ['COMPLETED', 'CLOSED'].includes(String(job.status || '').toUpperCase())
                    return (
                      <div
                        key={job.id}
                        className={`flex items-center justify-between rounded-md bg-surface-container-lowest p-3 ${isDone ? 'border-l-4 border-primary opacity-50' : 'border-l-4 border-error'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`material-symbols-outlined text-lg ${isDone ? 'text-primary' : 'text-error'}`}>
                            {isDone ? 'check_circle' : 'dangerous'}
                          </span>
                          <span className="text-xs font-bold">{job.job_name || job.job_code}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => actions.removeDependencyJob(job.id)}
                          className="text-[10px] font-medium text-on-surface-variant hover:text-error"
                        >
                          {isDone ? 'DONE' : 'PRE-REQ'}
                        </button>
                      </div>
                    )
                  })
                ) : (
                  <div className="rounded-md bg-surface-container-lowest p-3 text-xs font-medium text-on-surface-variant">
                    No dependency linked.
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setIsDependencyModalOpen(true)}
                  className="w-full rounded-md border-2 border-dashed border-outline-variant py-3 text-xs font-bold uppercase tracking-widest text-on-surface-variant transition-colors hover:bg-surface-container-high"
                >
                  Manage Map
                </button>

                <div className="pt-2">
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Responsible Persons</p>
                  <button
                    type="button"
                    onClick={openResponsiblePicker}
                    className="flex w-full items-center justify-between rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-left text-sm text-on-surface transition-colors hover:border-primary/40"
                  >
                    <span>{responsibleUsers.length ? `${responsibleUsers.length} selected` : 'Select responsible persons'}</span>
                    <span className="material-symbols-outlined text-base text-on-surface-variant">expand_more</span>
                  </button>
                </div>

                {responsibleUsers.length ? (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {responsibleUsers.map((user) => (
                      <AppTooltip
                        key={user.user_id}
                        content={
                          user.assignment_note
                            ? `${user.assignment_note}${user.is_completed ? '\nStatus: Completed' : ''}`
                            : 'No responsibility note added.'
                        }
                      >
                        <span
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${user.is_completed
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                            : 'border-outline-variant/40 bg-surface-container-lowest'
                            }`}
                        >
                          <span
                            className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-[10px] font-black ${user.is_completed ? 'bg-emerald-200 text-emerald-800' : 'bg-tertiary-fixed text-primary'
                              }`}
                          >
                            {user.priority_order}
                          </span>
                          <span className="text-xs font-semibold text-on-surface">{user.user_name}</span>
                          <button
                            type="button"
                            onClick={() => actions.toggleResponsibleCompleted(user.user_id)}
                            className="material-symbols-outlined text-sm text-on-surface-variant hover:text-emerald-700"
                            title={user.is_completed ? 'Mark incomplete' : 'Mark complete'}
                          >
                            done
                          </button>
                          <button
                            type="button"
                            onClick={() => actions.removeResponsibleUser(user.user_id)}
                            className="material-symbols-outlined text-sm text-on-surface-variant hover:text-error"
                            title="Remove responsible person"
                          >
                            close
                          </button>
                        </span>
                      </AppTooltip>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            {/* <section className="rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-6">
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-primary">Internal Actions</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className="flex flex-col items-center justify-center gap-2 rounded-md bg-white/80 p-4 transition-colors hover:bg-white">
                  <span className="material-symbols-outlined text-primary">history</span>
                  <span className="text-[10px] font-bold uppercase">Audit Log</span>
                </button>
                <button type="button" className="flex flex-col items-center justify-center gap-2 rounded-md bg-white/80 p-4 transition-colors hover:bg-white">
                  <span className="material-symbols-outlined text-primary">share</span>
                  <span className="text-[10px] font-bold uppercase">Share</span>
                </button>
              </div>
            </section> */}

            <section className="rounded-lg border border-outline-variant/20 bg-surface-container-low p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-on-surface">Mark Completed</span>
                <AppToggle
                  checked={Boolean(state.form.is_completed)}
                  onChange={() => actions.toggleCompleted()}
                  ariaLabel="Toggle job completion"
                />
              </div>
              {Boolean(state.form.is_completed) ? (
                <div className="mt-4 space-y-2">
                  <label className="grid gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                      Completion Note <span className="text-error">*</span>
                    </span>
                    <input
                      type="text"
                      value={state.form.completion_note}
                      onChange={(event) => actions.patchForm('completion_note', event.target.value)}
                      className="rounded-md border-none bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none"
                    />
                  </label>
                </div>
              ) : null}
            </section>

            <div className="flex items-center gap-3">
              {state.isEditMode ? (
                <button
                  type="button"
                  onClick={actions.handleArchive}
                  disabled={state.isSaving}
                  className="rounded-md px-6 py-2.5 text-lg font-semibold text-secondary transition-colors hover:bg-surface-container-high disabled:opacity-50"
                >
                  <span className="mr-2 material-symbols-outlined align-middle text-lg">archive</span>
                  Archive
                </button>
              ) : null}
              <button
                type="button"
                onClick={actions.handleDiscard}
                className="rounded-md bg-secondary-container px-6 py-2.5 text-lg font-bold text-on-secondary-container hover:bg-surface-container-highest"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={() => actions.handleSave(false)}
                disabled={state.isSaving || state.isLoading}
                className="rounded-md bg-primary px-8 py-2.5 text-lg font-bold text-on-primary shadow-lg shadow-primary/20 disabled:opacity-60"
              >
                <span className="mr-2 material-symbols-outlined align-middle text-lg">save</span>
                {state.success ? 'Success! ✓' : state.isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              {!state.isEditMode && !state.selectedAssignee && (
                <button
                  type="button"
                  onClick={() => actions.handleSaveAndAllocate()}
                  disabled={state.isSaving || state.isLoading}
                  className="rounded-md border border-primary bg-primary/10 px-6 py-2.5 text-base font-bold text-primary transition-all hover:bg-primary/20 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Save &amp; Allocate on Board (Real) &rarr;
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isDependencyModalOpen ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-bold text-on-surface">Manage Job Dependencies</h4>
              <button
                type="button"
                onClick={() => setIsDependencyModalOpen(false)}
                className="rounded-full p-1 hover:bg-surface-container-low"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="relative mb-3">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
                search
              </span>
              <input
                type="text"
                value={depSearch}
                onChange={(e) => setDepSearch(e.target.value)}
                placeholder="Search jobs..."
                className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest py-2.5 pl-10 pr-4 text-sm text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="rounded-md border border-outline-variant/30 bg-surface-container-lowest p-1">
              <AppScrollArea
                maxHeight="320px"
                onLoadMore={depHasMore ? () => { } : undefined}
                isLoading={depIsLoading}
                hasMore={depHasMore}
              >
                <div className="space-y-1 pr-1">
                  {depJobs.length ? (
                    depJobs.map((job) => {
                      const selected = state.form.dependency_job_ids.includes(Number(job.value))
                      return (
                        <button
                          key={job.value}
                          type="button"
                          onClick={() =>
                            selected ? actions.removeDependencyJob(job.value) : actions.addDependencyJob(job.value)
                          }
                          className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left ${selected ? 'bg-primary-fixed text-primary' : 'hover:bg-surface-container-low'
                            }`}
                        >
                          <div>
                            <p className="text-sm font-semibold">{job.label}</p>
                            <p className="text-xs opacity-75">{job.subLabel}</p>
                          </div>
                          <span className="text-xs">{selected ? 'Linked' : 'Link'}</span>
                        </button>
                      )
                    })
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-on-surface-variant">
                      {depIsLoading ? 'Loading jobs...' : 'No jobs found.'}
                    </div>
                  )}
                  {depHasMore ? <div ref={depSentinelRef} className="h-1" /> : null}
                </div>
              </AppScrollArea>
            </div>
          </div>
        </div>
      ) : null}

      {isResponsiblePickerOpen ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-lg font-bold text-on-surface">Responsible Persons</h4>
              <button type="button" onClick={cancelResponsiblePicker} className="rounded-full p-1 hover:bg-surface-container-low">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="relative mb-3">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
                search
              </span>
              <input
                type="text"
                value={responsibleSearch}
                onChange={(e) => setResponsibleSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest py-2.5 pl-10 pr-4 text-sm text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="rounded-md border border-outline-variant/30 bg-surface-container-lowest p-1">
              <AppScrollArea
                maxHeight="360px"
                onLoadMore={responsibleHasMore ? () => { } : undefined}
                isLoading={responsibleIsLoading}
                hasMore={responsibleHasMore}
              >
                <div className="space-y-1 pr-1">
                  {rawResponsibleOptions.length ? (
                    rawResponsibleOptions.map((user) => {
                      const userId = Number(user.value)
                      const selectedDependency = draftResponsibleUsers.find((row) => Number(row.user_id) === userId)
                      const selected = Boolean(selectedDependency)
                      return (
                        <div
                          key={`responsible-${user.value}`}
                          className={`rounded-lg border px-3 py-2 transition-colors ${selected ? 'border-primary/30 bg-primary-fixed/40' : 'border-transparent hover:bg-surface-container-low'
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleDraftResponsibleUser(user)}
                            className="flex w-full items-center justify-between text-left"
                          >
                            <div>
                              <p className="text-sm font-semibold text-on-surface">{user.label}</p>
                              <p className="text-xs text-on-surface-variant">{user.subLabel}</p>
                            </div>
                            <span className="text-xs font-semibold text-on-surface-variant">{selected ? 'Selected' : 'Select'}</span>
                          </button>
                          {selected ? (
                            <div className="mt-2 grid gap-1">
                              <label className="text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant">
                                Why responsible? <span className="text-error">*</span>
                              </label>
                              <input
                                type="text"
                                value={selectedDependency?.assignment_note || ''}
                                onChange={(event) => setDraftResponsibleNote(userId, event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                placeholder="Enter responsibility note"
                                className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface outline-none"
                              />
                            </div>
                          ) : null}
                        </div>
                      )
                    })
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-on-surface-variant">
                      {responsibleIsLoading ? 'Loading users...' : 'No users found.'}
                    </div>
                  )}
                  {responsibleHasMore ? <div ref={responsibleSentinelRef} className="h-1" /> : null}
                </div>
              </AppScrollArea>
            </div>
            {responsibleModalError ? (
              <p className="mt-3 text-sm font-semibold text-error">{responsibleModalError}</p>
            ) : null}
            <div className="mt-4 flex items-center justify-end gap-2 border-t border-outline-variant/20 pt-4">
              <button
                type="button"
                onClick={cancelResponsiblePicker}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-on-surface-variant transition-colors hover:bg-surface-container-low"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveResponsiblePicker}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-on-primary"
              >
                Save Responsible Persons
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isAvailabilityModalOpen && state.selectedAssignee && (
        <AllocationAssignmentModal
          employee={{
            employee_id: state.selectedAssignee.employee_id || state.selectedAssignee.id,
            name: state.selectedAssignee.name,
            base_hours_per_day: 8,
          }}
          preSelectedTask={{
            task_id: state.editingJobId || 0,
            task_code: state.jobCode || (state.editingJobId ? '' : 'NEW'),
            task_name: state.form.job_name || 'New Job',
            project_name: 'Project',
            planned_hours_value: state.form.planned_hours_value,
            planned_minutes_value: state.form.planned_minutes_value,
            planned_start_time: state.form.planned_start_time,
            planned_end_time: state.form.planned_end_time,
            planned_start: state.form.planned_start,
            planned_end: state.form.planned_end,
          }}
          month={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
          isUpdate={!!state.editingJobId}
          mode="select-job"

          onAssign={(payload) => {
            if (payload.planned_start) {
              actions.patchForm('planned_start', payload.planned_start)
            }
            if (payload.planned_end) {
              actions.patchForm('planned_end', payload.planned_end)
            }
            if (payload.planned_start_time !== undefined) {
              actions.patchForm('planned_start_time', payload.planned_start_time)
            }
            if (payload.planned_end_time !== undefined) {
              actions.patchForm('planned_end_time', payload.planned_end_time)
            }
            if (payload.planned_hours_value !== undefined) {
              actions.setPlannedHours(payload.planned_hours_value)
            }
            if (payload.planned_minutes_value !== undefined) {
              actions.setPlannedMinutes(payload.planned_minutes_value)
            }
            setIsAvailabilityModalOpen(false)
          }}
          onClose={() => setIsAvailabilityModalOpen(false)}
          onRefresh={() => {
            const empId = state.selectedAssignee.employee_id || state.selectedAssignee.id
            if (empId) refreshEmployee({ employee_id: empId })
          }}
          isRefreshing={isRefreshingEmployee}
        />
      )}

      <AppDocumentPreviewModal
        isOpen={Boolean(previewingFile)}
        onClose={() => setPreviewingFile(null)}
        fileUrl={previewingFile?.url || ''}
        fileName={previewingFile?.name || ''}
      />
    </div>
  )
}
